import { useState } from "react";
import algosdk from "algosdk";
import { useNavigate } from "react-router-dom";
import TermsModal from "./TermsModal";
import { useWallet } from "../context/WalletContext";
import { saveEncryptedWallet } from "../utils/secureStorage"; 
import { encryptMnemonic } from "../utils/crypto";
import "../styles/moxWallet.css";
import "../styles/general.css";

function isStrongPassword(pw: string): boolean {
  return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(pw);
}

interface RegisterProps {
  setMessage: (msg: string) => void;
}

const Register: React.FC<RegisterProps> = ({ setMessage }) => {
  const { setAccount } = useWallet();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [mnemonic, setMnemonic] = useState("");
  const [importMnemonic, setImportMnemonic] = useState("");
  const [wordIndices, setWordIndices] = useState<number[]>([]);
  const [wordAnswers, setWordAnswers] = useState<string[]>(["", "", ""]);
  const [step, setStep] = useState<"form" | "show" | "verify" | "warning">("form");
  const [mode, setMode] = useState<"create" | "import">("create");

  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(true);

  const handleAcceptTerms = () => {
    setShowTerms(false);
  };

  const generateVerificationIndices = () => {
    const indices = new Set<number>();
    while (indices.size < 3) {
      indices.add(Math.floor(Math.random() * 25));
    }
    return [...indices];
  };

  const handleCreate = () => {
    if (password !== passwordConfirm) {
      return setMessage("Las contraseñas no coinciden");
    }
    if (!isStrongPassword(password)) {
      return setMessage("La contraseña debe tener mínimo 8 caracteres, incluir letras, números y símbolos.");
    }

    const acc = algosdk.generateAccount();
    const mnem = algosdk.secretKeyToMnemonic(acc.sk);
    setMnemonic(mnem);
    setWordIndices(generateVerificationIndices());
    setStep("show");
  };

  const handleImport = () => {
    if (!importMnemonic || password !== passwordConfirm) {
      return setMessage("Revisa los campos");
    }

    if (!isStrongPassword(password)) {
      return setMessage("La contraseña debe tener mínimo 8 caracteres, incluir letras, números y símbolos.");
    }

    try {
      const cleanedMnemonic = importMnemonic.trim();
      algosdk.mnemonicToSecretKey(cleanedMnemonic); 
      setMnemonic(cleanedMnemonic); 
      setWordIndices(generateVerificationIndices());
      setStep("warning"); 
    } catch {
      setMessage("Mnemonic inválido");
    }
  };

  const handleVerify = async () => {
    const words = mnemonic.split(" ");
    const isValid = wordIndices.every((i, idx) => words[i] === wordAnswers[idx].trim().toLowerCase());

    if (!isValid) return setMessage("Palabras incorrectas");

    try {
      const acc = algosdk.mnemonicToSecretKey(mnemonic.trim());
      const encrypted = await encryptMnemonic(mnemonic.trim(), password);
      await saveEncryptedWallet(username, encrypted); 
      setAccount(acc, false, username);
      setMnemonic(""); 
      setMessage("✅ Cuenta activada");
      navigate("/home");
    } catch (err) {
      console.error("❌ Error al guardar cuenta:", err);
      setMessage("Error al guardar cuenta");
    }
  };

   return (
    <div>
      {showTerms && <TermsModal open={showTerms} onAccept={handleAcceptTerms} />}
      {!showTerms && (
        <>
          {step === "form" && (
            <div className="modal-container">
              <h2>
                {mode === "import" ? "Recuperar Wallet" : "Crear Wallet"}
              </h2>

              <input
                className="wallet-input"
                type="text"
                placeholder="Nombre de usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <input
                className="wallet-input"
                type="password"
                placeholder="Contraseña segura"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <input
                className="wallet-input"
                type="password"
                placeholder="Confirmar contraseña"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />

              {mode === "import" && (
                <textarea
                  className="wallet-input"
                  placeholder="Frase mnemónica (25 palabras)"
                  value={importMnemonic}
                  onChange={(e) => setImportMnemonic(e.target.value)}
                />
              )}

              <button
                className="button-primary"
                onClick={mode === "import" ? handleImport : handleCreate}
              >
                {mode === "import" ? "Recuperar Wallet" : "Crear Wallet"}
              </button>

              <button
                className="button-secondary toggle-button"
                onClick={() => setMode(mode === "import" ? "create" : "import")}
              >
              {mode === "import"
              ? "¿Deseas crear una nueva wallet?"
              : "¿Ya tienes una wallet? Recuperar"}
              </button>
              <button className="button-exit" onClick={() => navigate("/")}>
                Volver
              </button>
            </div>
          )}

          {step === "show" && mode === "create" && (
            <div className="wallet-container show-mnemonic">
              <h2 className="text-md font-semibold mb-2">Copia tu frase mnemónica</h2>
              <div className="mnemonic-grid">
                {mnemonic.split(" ").map((word, index) => (
                  <span key={index} className="mnemonic-word">
                    <strong>{index + 1}.</strong> {word}
                  </span>
                ))}
              </div>
              <h2>⚠️ ¡Importante!</h2>
              <p>
                Tu wallet se guarda solo en este navegador. Si borras tus datos, cambias de
                dispositivo o navegador, perderás el acceso a tu cuenta.
              </p>
              <p>📌 Guarda tu frase mnemónica en un lugar seguro.</p>
              <button className="wallet-button mt-4" onClick={() => setStep("verify")}>
                Ya la guardé
              </button>
            </div>
          )}

          {step === "warning" && mode === "import" && (
            <div className="wallet-container show-warning">
              <h3 className="text-md font-semibold mb-2">Frase importada correctamente</h3>
              <p>
                ⚠️ Esta frase no se volverá a mostrar. Asegúrate de haberla guardado en un
                lugar seguro.
              </p>
              <button className="wallet-button mt-4" onClick={() => setStep("verify")}>
                Verificar 3 palabras
              </button>
            </div>
          )}

          {step === "verify" && (
            <div className="wallet-container verify-words">
              <h3 className="text-md font-semibold mb-2">
                Verifica tu frase de recuperación
              </h3>
              {wordIndices.map((index, idx) => (
                <div key={index} className="w-full">
                  <label className="text-sm text-gray-500">
                    Palabra #{index + 1}:
                  </label>
                  <input
                    className="wallet-input"
                    type="text"
                    value={wordAnswers[idx]}
                    onChange={(e) => {
                      const updated = [...wordAnswers];
                      updated[idx] = e.target.value;
                      setWordAnswers(updated);
                    }}
                  />
                </div>
              ))}
              <button className="wallet-button mt-4" onClick={handleVerify}>
                Verificar y Activar
              </button>
              <button className="button-exit mt-4" onClick={() => navigate("/")}>
                Volver
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Register;